'use client';
// Qualification des accessoires et rattachement à l'appareil qu'ils servent.
//
// ── Pourquoi un panneau à part, et pourquoi il PROPOSE ──────────────────────
//
// Le parc compte 245 fiches, dont 154 informatiques : écrans, claviers, souris,
// onduleurs, imprimantes. Aucune ne mesure, et une carte de contrôle posée sur
// l'une d'elles suivrait la dérive d'un appareil qui ne produit aucun résultat.
// La nature de chaque fiche doit donc être POSÉE — mais 154 fiches basculées
// d'office seraient 154 décisions que personne n'a prises, et le jour où l'une
// se révèle fausse, plus rien ne dirait qu'elle a été devinée.
//
// Cet écran est donc le lieu où la déduction devient une DÉCISION : il montre
// ce que le serveur propose, AVEC SON MOTIF, et c'est un geste humain qui écrit.
// Deux propositions distinctes, dans cet ordre, parce que la seconde dépend de
// la première — on ne rattache un accessoire à un automate qu'une fois qu'il
// est déclaré accessoire :
//
//   1. QUALIFICATION — « cette fiche ne mesure rien » ;
//   2. RATTACHEMENT  — « et elle sert cet automate-là ».
//
// Les cas que le serveur REFUSE de proposer sont affichés au même titre que les
// autres. Les taire donnerait l'illusion d'un parc entièrement qualifié, ce qui
// est exactement le constat fabriqué qu'on cherche à éviter.
import React, { useCallback, useEffect, useState } from 'react';
import { useGed } from '@/lib/store';
import { I, card, EmptyState, useBusy } from '@/components/ui';

import { Bouton } from '@/shared/Bouton';
type Ligne = { documentId: string; code: string; nom: string; categorie: string; motif?: string; causes?: string[] };
type Nature = { ok: boolean; dejaQualifies: number; proposes: Ligne[]; bloques: Ligne[]; muets: Ligne[] };
type Suggestion = { accessoire: string; accessoireNom: string; automate: string; automateNom: string; motif: string };
type Ambigu = { accessoire: string; accessoireNom: string; candidats: string[]; motif: string };
type Rattachements = { ok: boolean; accessoires: number; dejaRattaches: number; suggestions: Suggestion[]; ambigus: Ambigu[] };

/** ⚠️ `/api/ged/ged/…` — DOUBLE `ged`. Le proxy Next est monté sur
 *  `/api/ged/`, et le moteur Strapi porte lui-même le préfixe `ged`. Un seul
 *  `ged` renvoie un 404 « Route non autorisée » que l'écran traduirait en liste
 *  vide, sans un mot : le parc paraîtrait entièrement qualifié. */
async function lire<T>(chemin: string): Promise<T | null> {
  const r = await fetch(`/api/ged/ged/${chemin}`, { credentials: 'include' });
  if (!r.ok) return null;
  return (await r.json().catch(() => null)) as T | null;
}

const titre: React.CSSProperties = { margin: 0, fontSize: 13.5, fontWeight: 700, color: 'var(--text-strong)' };
const aide: React.CSSProperties = { margin: 0, fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.5 };
const ligneStyle: React.CSSProperties = {
  display: 'flex', gap: 10, alignItems: 'baseline', flexWrap: 'wrap',
  padding: '8px 0', borderTop: '1px solid var(--border-subtle)', fontSize: 12.5,
};
const codeStyle: React.CSSProperties = {
  fontFamily: 'var(--font-mono)', fontSize: 12.5, fontWeight: 700, color: 'var(--text-muted)', minWidth: 60,
};

export function PanneauAccessoires() {
  const { session, smq, creerLien, refresh, showToast } = useGed();
  const isRQ = session.roles.includes('rq');
  const [nature, setNature] = useState<Nature | null>(null);
  const [ratt, setRatt] = useState<Rattachements | null>(null);
  const [chargement, setChargement] = useState(true);
  const { run, estOccupe } = useBusy();

  // Les deux lectures sont refaites ENSEMBLE après chaque écriture : qualifier
  // une fiche la fait entrer dans le calcul des rattachements, et laisser la
  // seconde liste périmée ferait proposer un rattachement pour une fiche qui
  // n'est plus proposable — ou taire celui d'une fiche qui vient de le devenir.
  //
  // ⚠️ Aucun `setState` SYNCHRONE ici : `react-hooks/set-state-in-effect` est
  // une erreur de lint dans ce dépôt, et poser `setChargement(true)` en tête de
  // cette fonction la ferait échouer dès qu'elle est appelée depuis l'effet de
  // montage. L'état de chargement part donc à `true` et ne redescend qu'après
  // l'attente ; les rappels manuels le relèvent eux-mêmes, depuis un
  // gestionnaire d'événement où la règle ne s'applique pas.
  const charger = useCallback(async () => {
    const [n, r] = await Promise.all([
      lire<Nature>('equip/nature-suggestions'),
      lire<Rattachements>('equip/accessoires-suggestions'),
    ]);
    setNature(n); setRatt(r); setChargement(false);
  }, []);

  // Le chargement initial passe par `.then()` et non par `await charger()` :
  // appelée directement dans un effet, une fonction `async` empêche de voir que
  // ses `setState` sont bien postérieurs au premier await, et
  // `react-hooks/set-state-in-effect` la refuse. Même forme que les effets de
  // l'écran des calibrations. `charger` reste employée par les gestes.
  useEffect(() => {
    let vivant = true;
    void Promise.all([
      lire<Nature>('equip/nature-suggestions'),
      lire<Rattachements>('equip/accessoires-suggestions'),
    ]).then(([n, r]) => {
      if (!vivant) return;
      setNature(n); setRatt(r); setChargement(false);
    });
    return () => { vivant = false; };
  }, []);

  if (chargement) {
    return <div style={{ ...card, padding: 20, fontSize: 12.5, color: 'var(--text-muted)' }}>Analyse du parc…</div>;
  }
  if (!nature || !ratt) {
    return (
      <div style={{ ...card, padding: 20 }}>
        <EmptyState titre="Les propositions n’ont pas pu être calculées."
          indice="Le moteur n’a pas répondu. Rien n’a été modifié ; réessayez."
          action={<Bouton taille="sm" onClick={() => void charger()}><I n="refresh" s={13} />Réessayer</Bouton>} />
      </div>
    );
  }

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      {/* ── 1. QUALIFICATION ────────────────────────────────────────────── */}
      <div style={{ ...card, padding: 18, display: 'grid', gap: 12 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
          <h3 style={titre}>Qualification — équipement de mesure ou accessoire</h3>
          <span style={{ flex: 1 }} />
          <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
            {nature.dejaQualifies} fiche(s) déjà déclarée(s) accessoire
          </span>
        </div>
        <p style={aide}>
          Un équipement de <strong>mesure</strong> produit un résultat : lui seul peut porter une
          carte de contrôle interne, une calibration ou un raccordement métrologique. Un
          <strong> accessoire</strong> ne mesure rien — l’ordinateur d’un automate, une imprimante,
          un onduleur, un clavier. La qualification proposée ci-dessous est déduite de la
          <em> désignation</em> de chaque fiche, jamais de sa catégorie : elle reste une déduction
          tant que personne ne l’a confirmée.
        </p>

        {nature.proposes.length > 0 && (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <strong style={{ fontSize: 12.5 }}>{nature.proposes.length} fiche(s) proposée(s) en accessoire</strong>
              {isRQ && (
                <Bouton variante="primaire" taille="sm" onClick={() => void run('qualifier', async () => {
                    const ok = await smq('ged/equip/nature-qualifier',
                      { documentIds: nature.proposes.map((p) => p.documentId) },
                      `${nature.proposes.length} fiche(s) déclarée(s) accessoire.`);
                    if (ok) await charger();
                  })} disabled={estOccupe()}>
                  <I n="check" s={13} />
                  {estOccupe('qualifier') ? 'Qualification…' : `Tout qualifier (${nature.proposes.length})`}
                </Bouton>
              )}
            </div>
            {/* La liste reste lisible ligne à ligne : c'est le motif qui rend le
                geste groupé acceptable — sans lui, « tout qualifier » serait un
                bouton qui bascule 154 fiches sans qu'on sache pourquoi. */}
            <div style={{ maxHeight: 260, overflowY: 'auto' }}>
              {nature.proposes.map((p) => (
                <div key={p.documentId} style={ligneStyle}>
                  <span style={codeStyle}>{p.code || '—'}</span>
                  <span style={{ fontWeight: 700, color: 'var(--text-strong)', minWidth: 180 }}>{p.nom}</span>
                  <span style={{ flex: '1 1 220px', color: 'var(--text-muted)' }}>{p.motif}</span>
                </div>
              ))}
            </div>
          </>
        )}
        {nature.proposes.length === 0 && (
          <p style={aide}>Aucune nouvelle fiche à qualifier d’après sa désignation.</p>
        )}

        {nature.bloques.length > 0 && (
          <div style={{ borderTop: '1px solid var(--border-default)', paddingTop: 10 }}>
            <strong style={{ fontSize: 12.5, color: 'var(--red-600)' }}>
              <I n="alert" s={12} /> {nature.bloques.length} fiche(s) que la désignation dit accessoire mais qui portent déjà un rattachement de mesure
            </strong>
            <p style={aide}>
              Les déclasser rendrait ces éléments orphelins, et aucun écran ne saurait les recréer.
              Retirez-les d’abord si l’appareil ne mesure réellement pas.
            </p>
            {nature.bloques.map((p) => (
              <div key={p.documentId} style={ligneStyle}>
                <span style={codeStyle}>{p.code || '—'}</span>
                <span style={{ fontWeight: 700, minWidth: 180 }}>{p.nom}</span>
                <span style={{ flex: '1 1 220px', color: 'var(--red-600)' }}>porte {(p.causes || []).join(', ')}</span>
              </div>
            ))}
          </div>
        )}

        {nature.muets.length > 0 && (
          <div style={{ borderTop: '1px solid var(--border-default)', paddingTop: 10 }}>
            <strong style={{ fontSize: 12.5 }}>{nature.muets.length} fiche(s) informatique(s) dont la désignation ne dit rien</strong>
            <p style={aide}>
              « Réception 5 - » ne dit pas de quel matériel il s’agit. Ces fiches ne sont pas
              proposées — les compter avec les précédentes donnerait l’illusion d’un parc
              entièrement qualifié. Ouvrez-les pour trancher, onglet « Identification ».
            </p>
            <div style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
              {nature.muets.map((m) => `${m.code} ${m.nom}`).join(' · ')}
            </div>
          </div>
        )}
      </div>

      {/* ── 2. RATTACHEMENT ─────────────────────────────────────────────── */}
      <div style={{ ...card, padding: 18, display: 'grid', gap: 12 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
          <h3 style={titre}>Rattachement — quel appareil cet accessoire sert-il ?</h3>
          <span style={{ flex: 1 }} />
          <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
            {ratt.dejaRattaches} rattaché(s) · {ratt.accessoires} en attente
          </span>
        </div>
        <p style={aide}>
          Une panne de clavier immobilise l’automate qu’il pilote : c’est ce lien-là qui permet
          de le dire. Chaque proposition est confirmée <em>une par une</em> — un rattachement
          deviné puis posé en base devient une vérité que personne n’a décidée, et qui sert
          ensuite à imputer des pannes.
        </p>

        {ratt.suggestions.map((s) => (
          <div key={s.accessoire} style={ligneStyle}>
            <span style={codeStyle}>{s.accessoire}</span>
            <span style={{ fontWeight: 700, minWidth: 150 }}>{s.accessoireNom}</span>
            <I n="arrow" s={12} />
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12.5, fontWeight: 700 }}>{s.automate}</span>
            <span style={{ flex: '1 1 160px', color: 'var(--text-muted)' }}>{s.automateNom}</span>
            {isRQ && (
              <Bouton taille="sm" onClick={() => void run(s.accessoire, async () => {
                  const ok = await creerLien({
                    sourceType: 'equipement', sourceRef: s.accessoire,
                    cibleType: 'equipement', cibleRef: s.automate,
                    typeLien: 'accessoire_de',
                  });
                  if (ok) await charger();
                })} disabled={estOccupe()}>
                <I n="check" s={12} />{estOccupe(s.accessoire) ? '…' : 'Confirmer'}
              </Bouton>
            )}
          </div>
        ))}
        {ratt.suggestions.length === 0 && (
          <p style={aide}>
            {ratt.accessoires === 0
              ? 'Aucun accessoire en attente de rattachement — qualifiez d’abord les fiches ci-dessus.'
              : 'Aucune désignation ne permet de proposer un rattachement sûr.'}
          </p>
        )}

        {ratt.ambigus.length > 0 && (
          <div style={{ borderTop: '1px solid var(--border-default)', paddingTop: 10 }}>
            <strong style={{ fontSize: 12.5 }}>{ratt.ambigus.length} désignation(s) ambiguë(s)</strong>
            <p style={aide}>
              « Onduleur INDIKO » désigne aussi bien l’INDIKO 1 que l’INDIKO 2 : l’étiquette de
              l’appareil doit trancher. Rattachez-les depuis l’onglet « Liens & NC » de la fiche.
            </p>
            {ratt.ambigus.map((a) => (
              <div key={a.accessoire} style={ligneStyle}>
                <span style={codeStyle}>{a.accessoire}</span>
                <span style={{ fontWeight: 700, minWidth: 150 }}>{a.accessoireNom}</span>
                <span style={{ flex: '1 1 220px', color: 'var(--text-muted)' }}>{a.candidats.join(' · ')}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {!isRQ && (
        <p style={aide}>
          Seul le responsable qualité peut poser une qualification ou un rattachement.
          {/* `refresh` et `showToast` restent employés par les gestes ci-dessus ;
              cette branche n'en montre aucun. */}
        </p>
      )}
      <Bouton taille="sm" onClick={() => { setChargement(true); void charger().then(() => { void refresh(); showToast('Propositions recalculées.'); }); }} style={{ width: 'fit-content' }}>
        <I n="refresh" s={13} />Recalculer
      </Bouton>
    </div>
  );
}
